/**
 * Estimation decks.
 *
 * A deck is just an ordered list of cards. Two properties drive everything
 * downstream:
 *
 * - `value` is what the statistics run on. Cards that express "I can't
 *   estimate this" (`?`) or "I need a break" (`☕`) carry `null` and are left
 *   out of every calculation — averaging them would invent agreement that the
 *   room never reached.
 * - `scale` says how those values may be combined. On a `numeric` deck the
 *   numbers are real quantities, so a mean is meaningful. On an `ordinal` deck
 *   (t-shirt sizes) the values are ranks: L is bigger than M, but "the average
 *   of S and L" is not a size. See `summarizeVotes` in `stats.ts`, which drops
 *   the mean for ordinal decks rather than printing a number nobody can act on.
 */

/** How the numbers behind a deck's cards may legitimately be combined. */
export type DeckScale = 'numeric' | 'ordinal';

/** Identifier of a built-in deck, or `custom` for a room-defined one. */
export type DeckId = 'fibonacci' | 'powers-of-two' | 't-shirt' | 'custom';

export interface Card {
  /** Stable identifier sent over the wire. Votes reference this, not the label. */
  readonly id: string;
  /** What the participant sees on the card face. */
  readonly label: string;
  /**
   * Numeric weight for statistics, or `null` for non-committal cards.
   * On an ordinal deck this is the rank, not a quantity.
   */
  readonly value: number | null;
}

export interface Deck {
  readonly id: DeckId;
  readonly name: string;
  readonly scale: DeckScale;
  readonly cards: readonly Card[];
}

/** "I cannot estimate this." */
export const UNKNOWN_CARD_ID = 'unknown';
/** "I need a break." Drawn as the site's mark rather than as its label. */
export const BREAK_CARD_ID = 'break';

/**
 * Cards every deck ends with: "no idea" and "I need a break".
 *
 * These are not optional garnish. A deck without them forces a participant who
 * cannot estimate to either stall the round or invent a number, and an invented
 * number is indistinguishable from a real one once the votes are averaged. Both
 * are appended by `withEscapeCards`, and `hasEscapeCards` re-checks any deck
 * that reaches a room, including one assembled from labels a host typed.
 */
const ESCAPE_CARDS: readonly Card[] = [
  { id: UNKNOWN_CARD_ID, label: '?', value: null },
  { id: BREAK_CARD_ID, label: '☕', value: null },
];

/**
 * How many estimate cards a room gets when the host does not say.
 *
 * Seven is a starting point, not a law: the host picks the number when they
 * create the room and `trimDeck` applies it. Trimming runs from the bottom of
 * the scale up, because that is where estimation actually happens — a round
 * spent choosing between 55 and 89 is a round the team should have spent
 * splitting the story.
 */
export const DEFAULT_DECK_CARDS = 7;

/**
 * Floor on a trimmed deck. Two cards is the least that can express
 * disagreement; one would make every round unanimous by construction.
 */
export const MIN_DECK_CARDS = 2;

/** Builds a numeric deck where each card's label is its own value. */
function numericCards(values: readonly number[]): readonly Card[] {
  return values.map((value) => ({ id: String(value), label: String(value), value }));
}

/**
 * Assembles a deck's card list: the estimate cards in the order given,
 * followed by the escape cards.
 *
 * Anything in `estimates` that duplicates an escape card is dropped first, so
 * a host who types "?" among their custom labels gets one such card rather
 * than two buttons that mean the same thing.
 */
function withEscapeCards(estimates: readonly Card[]): readonly Card[] {
  const reserved = new Set(ESCAPE_CARDS.flatMap((card) => [card.id, card.label]));
  return [
    ...estimates.filter((card) => !reserved.has(card.id) && !reserved.has(card.label)),
    ...ESCAPE_CARDS,
  ];
}

/**
 * The cards that name an estimate, escape cards aside.
 *
 * Identified by id rather than by `value === null`: on a custom deck a label
 * that is not a number also carries a null value, and those are still cards
 * the room votes on.
 */
export function estimateCards(deck: Deck): readonly Card[] {
  const escapes = new Set(ESCAPE_CARDS.map((card) => card.id));
  return deck.cards.filter((card) => !escapes.has(card.id));
}

/**
 * Clamps a requested card count to something the deck can actually serve.
 *
 * Both sides run this: the form to bound its selector, the server because the
 * number arrives in a socket frame and may be anything at all.
 */
export function normalizeDeckCards(value: unknown, available: number): number {
  const ceiling = Math.max(MIN_DECK_CARDS, available);
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return Math.min(DEFAULT_DECK_CARDS, ceiling);
  }
  return Math.min(Math.max(Math.round(value), MIN_DECK_CARDS), ceiling);
}

/**
 * A copy of `deck` holding only its lowest `count` estimate cards. The escape
 * cards are outside the count and always survive — `?` and `☕` are not
 * estimates, and a deck without them is refused on room creation.
 */
export function trimDeck(deck: Deck, count: unknown): Deck {
  const estimates = estimateCards(deck);
  const limit = normalizeDeckCards(count, estimates.length);
  if (limit >= estimates.length) return deck;
  return { ...deck, cards: withEscapeCards(estimates.slice(0, limit)) };
}

/**
 * Whether a deck offers both escape cards. Every deck built here does by
 * construction; this exists so the server can refuse a deck that somehow does
 * not, rather than opening a room whose participants have no way to say "I
 * can't estimate this".
 */
export function hasEscapeCards(deck: Deck): boolean {
  return ESCAPE_CARDS.every((escape) => deck.cards.some((card) => card.id === escape.id));
}

export const FIBONACCI_DECK: Deck = {
  id: 'fibonacci',
  name: 'Fibonacci',
  scale: 'numeric',
  cards: withEscapeCards([
    // Written out rather than left to `numericCards`, which prints a card's own
    // value: the half card is worth 0.5 to the statistics but reads as ½ on the
    // table, the way a physical deck has it.
    { id: '0.5', label: '½', value: 0.5 },
    ...numericCards([1, 2, 3, 5, 8, 13, 21, 34, 55, 89]),
  ]),
};

export const POWERS_OF_TWO_DECK: Deck = {
  id: 'powers-of-two',
  name: 'Powers of 2',
  scale: 'numeric',
  cards: withEscapeCards(numericCards([0, 1, 2, 4, 8, 16, 32, 64])),
};

/**
 * Sizes carry rank values so median and consensus still work; `scale:
 * 'ordinal'` is what stops the mean from being reported.
 */
export const T_SHIRT_DECK: Deck = {
  id: 't-shirt',
  name: 'T-shirt sizes',
  scale: 'ordinal',
  cards: withEscapeCards([
    { id: 'xs', label: 'XS', value: 1 },
    { id: 's', label: 'S', value: 2 },
    { id: 'm', label: 'M', value: 3 },
    { id: 'l', label: 'L', value: 4 },
    { id: 'xl', label: 'XL', value: 5 },
    { id: 'xxl', label: 'XXL', value: 6 },
  ]),
};

export const BUILT_IN_DECKS: Readonly<Record<Exclude<DeckId, 'custom'>, Deck>> = {
  fibonacci: FIBONACCI_DECK,
  'powers-of-two': POWERS_OF_TWO_DECK,
  't-shirt': T_SHIRT_DECK,
};

export const DEFAULT_DECK_ID: DeckId = 'fibonacci';

export const MAX_CARD_LABEL_LENGTH = 8;

/**
 * Builds a custom deck from raw labels. A label that parses as a finite number
 * keeps that number, so a team typing "1, 2, 4" still gets real averages;
 * anything else is treated as non-committal rather than guessing a rank.
 *
 * The list is not capped here: `trimDeck` applies the room's card count to a
 * custom deck exactly as it does to a built-in one.
 */
export function createCustomDeck(labels: readonly string[]): Deck {
  return {
    id: 'custom',
    name: 'Custom',
    scale: 'numeric',
    cards: withEscapeCards(
      labels.map((label, index) => {
        const parsed = Number(label);
        return {
          id: `custom-${index}`,
          label,
          value: label.trim() !== '' && Number.isFinite(parsed) ? parsed : null,
        };
      }),
    ),
  };
}

export function findCard(deck: Deck, cardId: string): Card | undefined {
  return deck.cards.find((card) => card.id === cardId);
}
